import { useCallback, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import {
  AddIcon,
  ConfirmDialog,
  DeleteIcon,
  DownloadTemplateIcon,
  EmptyState,
  ExportIcon,
  ScrollTable,
  UpdateFromFileIcon,
  UploadIcon,
  type ScrollTableColumn,
} from '@platform/ui-kit';
import { api, type AprImportResult, type AprRunRate } from '../../api';
import { describeError, useAsyncData } from '../admin/useAsyncData';
import { FilterBar } from './FilterBar';
import { RunRateDetailDrawer } from './RunRateDetailDrawer';
import { HelpPanel, type HelpContent } from './HelpPanel';
import { TOOLBAR } from './toolbarStyles';
import { ScreenHeader } from './ScreenHeader';
import { StatsStrip } from './StatsStrip';
import { MATCH_COLUMN, RUN_RATE_COLUMNS, RUN_RATE_SAMPLES } from './columns';
import {
  EMPTY_FILTERS,
  csvFromColumns,
  datedFilename,
  downloadFile,
  hasColumn,
  isReconciled,
  mapRow,
  formatCompactCurrency,
  formatCurrency,
  hasActiveFilter,
  matchesFilter,
  matchesText,
  parseCsv,
  pickTextFile,
  type FilterValues,
} from './shared';

/**
 * Current Run Rate.
 *
 * What the portfolio costs to run for a year, broken into its five components. This
 * is the baseline every rationalization case is argued against, so the components stay
 * visible rather than being collapsed into a single number.
 *
 * Laid out to match Application Master Data and APR's own screen: title with the
 * actions on the right, statistics strip, filter row, grid.
 */

/**
 * Money columns, to APR's measurements.
 *
 * Right-aligned with tabular figures so the digits line up down the column and a total
 * can be scanned rather than read. No colour of its own — the figures take the row's,
 * which keeps them black on an ordinary row and blue on a parent roll-up.
 */
function costColumn(
  field: keyof AprRunRate,
  label: string,
  width: string,
  tint?: (row: AprRunRate) => string | undefined,
): ScrollTableColumn<AprRunRate> {
  return {
    key: field,
    label,
    width,
    align: 'right',
    render: (row) => {
      const colour = tint?.(row);
      // A bare <span>: the cell carries the font from the table rule, and a Typography
      // per money cell is 5,211 emotion classes on this screen alone.
      return (
        <span
          style={{
            fontVariantNumeric: 'tabular-nums',
            ...(colour ? { color: colour, fontWeight: 600 } : {}),
          }}
        >
          {formatCurrency(row[field] as number)}
        </span>
      );
    },
  };
}

/** APR's TCO badges, shown against the application name. */
const BADGE_BASE = {
  px: '6px',
  py: '2px',
  fontSize: 9,
  fontWeight: 600,
  borderRadius: '12px',
  whiteSpace: 'nowrap',
  flexShrink: 0,
  lineHeight: 1.4,
} as const;

const PARENT_PILL_SX = {
  ...BADGE_BASE,
  fontSize: 11,
  color: '#ffffff',
  bgcolor: '#1976d2',
} as const;

const CHILD_PILL_SX = {
  ...BADGE_BASE,
  fontSize: 10,
  fontWeight: 500,
  color: '#1565c0',
  bgcolor: '#e3f2fd',
} as const;

/**
 * The TCO badge, all four states verbatim from APR (CurrentRunRate.tsx:2105).
 *
 * Anything that is not Defined, Bundled or Free falls to the red state, which is what
 * the source's trailing ternary does — including a null status, shown as "Not Defined".
 */
const TCO_BADGE_SX: Record<string, typeof BADGE_BASE & { color: string; bgcolor: string }> = {
  Defined: { ...BADGE_BASE, color: '#006100', bgcolor: '#C6EFCE' },
  Bundled: { ...BADGE_BASE, color: '#1565c0', bgcolor: '#BDD7EE' },
  Free: { ...BADGE_BASE, color: '#2e7d32', bgcolor: '#E2EFDA' },
  'Not Defined': { ...BADGE_BASE, color: '#9C0006', bgcolor: '#FFC7CE' },
};

const tcoBadgeSx = (status: string | null) =>
  TCO_BADGE_SX[status ?? ''] ?? TCO_BADGE_SX['Not Defined']!;

/**
 * Money colours, verbatim from APR (`getTotalColor` / `getParentCostColor`, line 873).
 *
 * The TOTAL is always coloured and always 600: purple on a parent roll-up, red when it
 * disagrees with its components by more than a penny, green otherwise. A row whose
 * components are all zero counts as agreeing — the source returns the match colour
 * rather than flagging an empty record.
 *
 * The five COMPONENT columns are coloured only on a parent.
 */
const TOTAL_MATCH_COLOR = '#1b7a2b';
const TOTAL_MISMATCH_COLOR = '#dc2626';
const PARENT_SUMMARY_COLOR = '#6d28d9';

/** Row actions, boxed as APR boxes them: 40×40, 1.25px border, 4px radius. */
const ACTION_BUTTON_SX = {
  width: 40,
  height: 40,
  p: '4px 6px',
  borderRadius: '4px',
  border: '1.25px solid',
  borderColor: '#1976d2',
  color: '#1976d2',
  bgcolor: 'transparent',
} as const;

const DANGER_BUTTON_SX = {
  ...ACTION_BUTTON_SX,
  borderColor: '#d32f2f',
  color: '#d32f2f',
} as const;

const GLYPH_SX = { fontSize: 14, lineHeight: 1 } as const;

const COST_INPUTS = [
  { key: 'total_annual_cost', label: 'Total Annual Cost' },
  { key: 'annual_software_licensing_fees', label: 'Software Licensing' },
  { key: 'annual_third_party_vendor_support', label: 'Vendor Support' },
  { key: 'annual_infrastructure_cost', label: 'Infrastructure' },
  { key: 'indirect_operational_costs', label: 'Operational Costs' },
  { key: 'cloud_saas_subscription_cost_impact', label: 'Cloud / SaaS' },
] as const;

const NEW_COST: Record<string, string> = {
  application_id: '',
  application_name: '',
  total_annual_cost: '0',
  annual_software_licensing_fees: '0',
  annual_third_party_vendor_support: '0',
  annual_infrastructure_cost: '0',
  indirect_operational_costs: '0',
  cloud_saas_subscription_cost_impact: '0',
  tco_status: 'Not Defined',
};

const TCO_STATUSES = ['Not Defined', 'Draft', 'In Review', 'Approved'] as const;

/** Help copy, verbatim from APR. Data, so it can be rewritten without touching the screen. */
const HELP: HelpContent = {
  icon: '💰',
  title: 'Current Run Rate',
  purpose:
    'Capture the annual cost to operate and support each application — the current "run rate" that later disposition decisions are measured against for savings.',
  doHere: [
    'Click Add Cost to record costs for one application, or edit/view any existing cost record from the table.',
    'Enter the Total Annual Cost plus its component costs (software licensing, vendor support, infrastructure, operational, cloud/SaaS).',
    'Use Upload File to import cost records in bulk — grab Download Template first.',
    'Filter by application, vendor, business function, type, or criticality; Download Data exports the current costs as CSV.',
    'Delete All Data clears every cost record.',
  ],
  fits: 'Portfolio → Cost',
  firstTime:
    "Download the template, fill in each application's annual costs, then Upload File to load them all.",
  sections: [
    {
      label: 'Cost fields',
      definitions: [
        {
          term: 'Total Annual Cost',
          color: '#1b7a2b',
          definition:
            'Full yearly cost to operate and support the app; it should reconcile with the sum of the component costs.',
        },
        {
          term: 'Parent roll-up',
          color: '#6d28d9',
          definition:
            'Parent-application rows show a summary of their children and are excluded from portfolio totals.',
        },
        {
          term: 'Mismatch',
          color: '#dc2626',
          definition:
            'The detail drawer shows the components total against the recorded total, and flags the gap when they disagree rather than silently correcting it.',
        },
      ],
    },
  ],
  links: [{ label: 'Application Master Data', path: '/apr/portfolio/applications' }],
};

export function CurrentRunRateTab() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const runRate = useAsyncData(() => api.apr.getRunRate());
  const session = useAsyncData(() => api.session());
  // Client Administrator, or any Talogent account — the same gate the server enforces.
  const isAdmin = Boolean(session.data?.user?.isAdmin || session.data?.user?.canAccessTalogent);
  const applications = useAsyncData(() => api.apr.getApplications());
  // Only applications with no cost record yet — the server excludes the rest, so Add
  // Cost cannot create a second record against the same application.
  const availableApplications = useAsyncData(() => api.apr.getAvailableApplications());

  const [text, setText] = useState('');
  const [filters, setFilters] = useState<FilterValues>(EMPTY_FILTERS);
  const [mode, setMode] = useState<'view' | 'edit'>('view');
  const [busy, setBusy] = useState(false);
  const [importResult, setImportResult] = useState<AprImportResult>();
  const [actionError, setActionError] = useState<string>();
  const [notice, setNotice] = useState<string>();
  const [adding, setAdding] = useState<Record<string, string>>();
  const [confirmWipe, setConfirmWipe] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<AprRunRate>();

  // Memoized so the `?? []` fallback does not mint a new array each render and
  // invalidate every derived useMemo below it.
  const rows = useMemo(() => runRate.data?.costs ?? [], [runRate.data]);

  // `appId` is shared with Application Master Data, so the cross-link from an
  // application opens that application's cost record here.
  const selectedId = params.get('appId');
  const selected = useMemo(
    () => rows.find((r) => r.id === selectedId || r.application_id === selectedId) ?? null,
    [rows, selectedId],
  );

  const openCost = useCallback(
    (id: string | null, next: 'view' | 'edit' = 'view') => {
      setMode(next);
      setParams((current) => {
        const next = new URLSearchParams(current);
        if (id) next.set('appId', id);
        else next.delete('appId');
        return next;
      });
    },
    [setParams],
  );

  // Cost rows carry no vendor or business function of their own — those live on the
  // application. Looked up here so the same four filters work on both screens.
  const applicationById = useMemo(
    () => new Map((applications.data?.applications ?? []).map((a) => [a.id, a])),
    [applications.data],
  );

  /**
   * Parent rows display the SUM of their children, never their own stored figures.
   *
   * Computed here and never written back, so a roll-up cannot drift from the children
   * it summarises. The stored row is left exactly as ported.
   */
  const displayed = useMemo(() => {
    const childrenOf = new Map<string, AprRunRate[]>();
    for (const row of rows) {
      const parentId = applicationById.get(row.application_id)?.parent_application_id;
      if (!parentId) continue;
      const group = childrenOf.get(parentId);
      if (group) group.push(row);
      else childrenOf.set(parentId, [row]);
    }

    return rows.map((row) => {
      const children = childrenOf.get(row.application_id);
      if (!children?.length) return row;
      const sum = (field: keyof AprRunRate) =>
        children.reduce((total, child) => total + (Number(child[field]) || 0), 0);
      return {
        ...row,
        total_annual_cost: sum('total_annual_cost'),
        annual_software_licensing_fees: sum('annual_software_licensing_fees'),
        annual_third_party_vendor_support: sum('annual_third_party_vendor_support'),
        annual_infrastructure_cost: sum('annual_infrastructure_cost'),
        indirect_operational_costs: sum('indirect_operational_costs'),
        cloud_saas_subscription_cost_impact: sum('cloud_saas_subscription_cost_impact'),
      };
    });
  }, [rows, applicationById]);

  const filtered = useMemo(
    () =>
      displayed.filter((row) => {
        const app = applicationById.get(row.application_id);
        return (
          matchesText(text, [
            row.application_name,
            row.application_id_code,
            row.tco_status,
            row.total_annual_cost,
          ]) &&
          matchesFilter(filters.vendor, app?.vendor_supplier) &&
          matchesFilter(filters.businessFunction, app?.business_function_domain) &&
          matchesFilter(filters.applicationType, app?.application_type) &&
          matchesFilter(filters.businessCriticality, app?.business_criticality)
        );
      }),
    [displayed, text, filters, applicationById],
  );

  /**
   * Parent applications are roll-ups: their children already carry the cost, so
   * counting both double-counts the portfolio. APR excludes them from every figure in
   * the strip, which is why its total reads $106.6M against $119.5M for all rows.
   */
  const costed = useMemo(
    () => filtered.filter((row) => !applicationById.get(row.application_id)?.is_parent),
    [filtered, applicationById],
  );

  const sum = (field: keyof AprRunRate) =>
    costed.reduce((total, row) => total + (Number(row[field]) || 0), 0);

  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setActionError(undefined);
    try {
      await fn();
      runRate.reload();
    } catch (err) {
      setActionError(describeError(err));
    } finally {
      setBusy(false);
    }
  };

  const upload = async (uploadMode: 'create' | 'update') => {
    setBusy(true);
    setImportResult(undefined);
    setActionError(undefined);
    try {
      const csv = await pickTextFile();
      if (csv === null) return;

      const raw = parseCsv(csv);
      if (raw.length === 0) {
        setActionError('That file had no data rows. Download the template to get the format.');
        return;
      }

      // An update file must say WHICH records to update. Without the key column every
      // row would be unmatchable, so reject the file rather than report 576 failures.
      if (uploadMode === 'update' && !hasColumn(raw[0], MATCH_COLUMN)) {
        setActionError(
          'Update file must contain an Application ID column to identify which cost records to update.',
        );
        return;
      }

      const rows = raw.map((row) => mapRow(row, RUN_RATE_COLUMNS));
      setImportResult(
        uploadMode === 'create'
          ? await api.apr.bulkImportRunRate(rows)
          : await api.apr.bulkUpdateRunRate(rows),
      );
      runRate.reload();
    } catch (err) {
      setActionError(describeError(err));
    } finally {
      setBusy(false);
    }
  };

  // Widths are APR's percentages, verbatim from its <colgroup> (CurrentRunRate.tsx:2009)
  // — 10/16/12/10/10/10/10/10/12, totalling 100.
  const columns: ScrollTableColumn<AprRunRate>[] = [
    {
      key: 'application_id_code',
      label: 'Application ID',
      width: '10%',
      render: (row) =>
        row.application_id_code ? (
          // APR prints the ID in <strong>, amber when this row's drawer is open.
          <Box
            component="strong"
            sx={{ fontSize: 13 }}
            style={selected?.id === row.id ? { color: '#b45309' } : undefined}
          >
            {row.application_id_code}
          </Box>
        ) : (
          // Unlinked rows are shown, not hidden — dropping them would understate the
          // portfolio's run rate without saying so.
          <Typography
            variant="body2"
            sx={{ fontSize: 13, color: 'text.disabled' }}
            title="No matching application record"
          >
            —
          </Typography>
        ),
    },
    {
      key: 'application_name',
      label: 'Application Name',
      width: '16%',
      render: (row) => {
        const app = applicationById.get(row.application_id);
        const parentCode = app?.parent_application_id
          ? (applicationById.get(app.parent_application_id)?.application_id_code ?? null)
          : null;
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Typography
              variant="body2"
              sx={{ fontSize: 13, fontWeight: 600, whiteSpace: 'normal', lineHeight: 1.3 }}
            >
              {row.application_name}
            </Typography>
            {app?.is_parent ? (
              <Box sx={PARENT_PILL_SX}>Parent ({app.child_count} children)</Box>
            ) : null}
            {parentCode ? <Box sx={CHILD_PILL_SX}>Child of {parentCode}</Box> : null}
            {/* A parent is a roll-up; it carries no TCO status of its own. */}
            {app?.is_parent ? null : (
              <Box sx={tcoBadgeSx(row.tco_status)}>{row.tco_status || 'Not Defined'}</Box>
            )}
          </Box>
        );
      },
    },
    costColumn('total_annual_cost', 'Total Annual Cost', '12%', (row) =>
      applicationById.get(row.application_id)?.is_parent
        ? PARENT_SUMMARY_COLOR
        : isReconciled(row)
          ? TOTAL_MATCH_COLOR
          : TOTAL_MISMATCH_COLOR,
    ),
    // The five components carry a colour only on a parent roll-up.
    ...(
      [
        ['annual_software_licensing_fees', 'Software Licensing'],
        ['annual_third_party_vendor_support', '3rd Party Vendor'],
        ['annual_infrastructure_cost', 'Infrastructure'],
        ['indirect_operational_costs', 'Indirect Ops'],
        ['cloud_saas_subscription_cost_impact', 'Cloud/SaaS'],
      ] as const
    ).map(([field, label]) =>
      costColumn(field, label, '10%', (row) =>
        applicationById.get(row.application_id)?.is_parent ? PARENT_SUMMARY_COLOR : undefined,
      ),
    ),
    {
      key: 'actions',
      label: 'Actions',
      width: '12%',
      align: 'center',
      render: (row) => (
        <Stack
          direction="row"
          spacing={0.5}
          justifyContent="center"
          sx={{ width: '100%' }}
          onClick={(e) => e.stopPropagation()}
        >
          <Tooltip title="View cost record">
            <IconButton
              sx={ACTION_BUTTON_SX}
              onClick={() => openCost(row.id, 'view')}
              aria-label="View cost record"
            >
              <Box component="span" sx={GLYPH_SX}>
                👁️
              </Box>
            </IconButton>
          </Tooltip>
          <Tooltip title="Edit cost record">
            <IconButton
              sx={ACTION_BUTTON_SX}
              onClick={() => openCost(row.id, 'edit')}
              aria-label="Edit cost record"
            >
              <Box component="span" sx={GLYPH_SX}>
                ✏️
              </Box>
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete cost record">
            <IconButton
              sx={DANGER_BUTTON_SX}
              onClick={() => setConfirmDelete(row)}
              aria-label="Delete cost record"
            >
              <Box component="span" sx={GLYPH_SX}>
                🗑️
              </Box>
            </IconButton>
          </Tooltip>
        </Stack>
      ),
    },
  ];

  const number = (value: number) => value.toLocaleString('en-US');

  return (
    <Box>
      <ScreenHeader
        title="Current Run Rate"
        actionsPosition="inline"
        aside={<HelpPanel help={HELP} onNavigate={navigate} />}
        trail={[{ label: 'Portfolio', path: '/apr/portfolio' }, { label: 'Current Run Rate' }]}
        onNavigate={navigate}
        actions={
          <>
            <Button
              variant="contained"
              onClick={() => setAdding({ ...NEW_COST })}
              disabled={busy}
              sx={TOOLBAR.add}
              startIcon={<AddIcon />}
            >
              Add Cost
            </Button>
            <Button
              variant="outlined"
              sx={TOOLBAR.template}
              startIcon={<DownloadTemplateIcon />}
              onClick={() =>
                downloadFile(
                  'current_run_rate_template.csv',
                  csvFromColumns(RUN_RATE_COLUMNS, RUN_RATE_SAMPLES),
                )
              }
            >
              Download Template
            </Button>
            <Button
              variant="outlined"
              onClick={() => void upload('create')}
              disabled={busy}
              sx={TOOLBAR.create}
              startIcon={<UploadIcon />}
            >
              Upload File
            </Button>
            <Button
              variant="outlined"
              onClick={() => void upload('update')}
              disabled={busy}
              sx={TOOLBAR.update}
              startIcon={<UpdateFromFileIcon />}
            >
              Update via File
            </Button>
            {/* Administrator only, and hidden rather than disabled for everyone else —
                the server refuses it regardless. */}
            {isAdmin ? (
              <Button
                variant="contained"
                onClick={() => setConfirmWipe(true)}
                disabled={busy || rows.length === 0}
                sx={TOOLBAR.danger}
                startIcon={<DeleteIcon />}
              >
                Delete All Data
              </Button>
            ) : null}
            <Button
              variant="outlined"
              sx={TOOLBAR.download}
              startIcon={<ExportIcon />}
              disabled={rows.length === 0}
              onClick={() =>
                downloadFile(
                  datedFilename('current_run_rate'),
                  csvFromColumns(
                    RUN_RATE_COLUMNS,
                    filtered.map((r) => ({
                      ...r,
                      application_id_code: r.application_id_code ?? '',
                    })),
                  ),
                )
              }
            >
              Download Data
            </Button>
          </>
        }
      />

      <StatsStrip
        stats={[
          {
            label: `${hasActiveFilter(text, filters) ? 'Filtered' : 'Total'} Applications`,
            value: number(costed.length),
          },
          {
            label: 'Total Annual Operating Cost',
            value: formatCompactCurrency(sum('total_annual_cost')),
          },
          {
            label: 'Total Software Licensing',
            value: formatCompactCurrency(sum('annual_software_licensing_fees')),
          },
          {
            label: 'Total Infrastructure Cost',
            value: formatCompactCurrency(sum('annual_infrastructure_cost')),
          },
          {
            label: 'Total Cloud/SaaS Costs',
            value: formatCompactCurrency(sum('cloud_saas_subscription_cost_impact')),
          },
        ]}
      />

      <FilterBar
        searchPlaceholder="Filter by application ID, name, or cost amounts..."
        text={text}
        onTextChange={setText}
        filters={filters}
        onFiltersChange={setFilters}
        {...(hasActiveFilter(text, filters)
          ? { summary: `${filtered.length} of ${rows.length} shown` }
          : {})}
      />

      <Stack spacing={2}>
        {runRate.error ? <Alert severity="error">{runRate.error}</Alert> : null}
        {actionError ? (
          <Alert severity="error" onClose={() => setActionError(undefined)}>
            {actionError}
          </Alert>
        ) : null}
        {notice ? (
          <Alert severity="success" onClose={() => setNotice(undefined)}>
            {notice}
          </Alert>
        ) : null}
        {importResult ? (
          <Alert
            severity={importResult.problems.length > 0 ? 'warning' : 'success'}
            onClose={() => setImportResult(undefined)}
          >
            <Typography variant="body2">
              {importResult.created} created · {importResult.updated} updated ·{' '}
              {importResult.skipped} skipped
            </Typography>
            {importResult.problems.length > 0 ? (
              <Box component="ul" sx={{ m: 0, mt: 1, pl: 2.5 }}>
                {importResult.problems.slice(0, 10).map((problem) => (
                  <Typography component="li" variant="body2" key={problem}>
                    {problem}
                  </Typography>
                ))}
                {importResult.problems.length > 10 ? (
                  <Typography component="li" variant="body2" color="text.secondary">
                    …and {importResult.problems.length - 10} more.
                  </Typography>
                ) : null}
              </Box>
            ) : null}
          </Alert>
        ) : null}

        {runRate.loading ? (
          <Box sx={{ p: 6, display: 'grid', placeItems: 'center' }}>
            <CircularProgress />
          </Box>
        ) : filtered.length === 0 ? (
          <EmptyState
            title={
              hasActiveFilter(text, filters)
                ? 'No cost records match this filter'
                : 'No cost records yet'
            }
            description={
              hasActiveFilter(text, filters)
                ? 'Clear the filter to see the full portfolio.'
                : 'Add one, or load a CSV with Upload File.'
            }
          />
        ) : (
          // Every row on one scrolling surface, exactly as APR has it — no pager.
          <ScrollTable<AprRunRate>
            rows={filtered}
            columns={columns}
            getRowId={(row) => row.id}
            onRowClick={(row) => openCost(row.id)}
            // APR's row tinting, verbatim (CurrentRunRate.tsx:2068). NOTE these are NOT
            // the Applications screen's tints — APR uses a paler blue and a paler grey
            // here, and the two screens genuinely differ.
            getRowStyle={(row) => {
              const app = applicationById.get(row.application_id);
              return {
                ...(app?.is_parent
                  ? { backgroundColor: '#e3f2fd' }
                  : app?.parent_application_id
                    ? { backgroundColor: '#f8f9fa' }
                    : {}),
                // The open drawer's row wins over the parent/child tint.
                ...(selected?.id === row.id
                  ? { backgroundColor: '#fff3cd', boxShadow: 'inset 3px 0 0 #f59e0b' }
                  : {}),
              };
            }}
          />
        )}
      </Stack>

      <RunRateDetailDrawer
        cost={selected}
        mode={mode}
        onModeChange={setMode}
        onClose={() => openCost(null)}
        onSaved={() => runRate.reload()}
        applications={applications.data?.applications ?? []}
      />

      {/* Add Cost — the application is chosen from master data, which fills the name
          so the two records cannot disagree about what the application is called. */}
      <Dialog open={Boolean(adding)} onClose={() => setAdding(undefined)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Cost</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              select
              label="Application"
              size="small"
              fullWidth
              required
              value={adding?.application_id ?? ''}
              onChange={(e) => {
                const app = (availableApplications.data?.applications ?? []).find(
                  (a) => a.id === e.target.value,
                );
                setAdding((c) =>
                  c
                    ? {
                        ...c,
                        application_id: e.target.value,
                        application_name: app?.application_name ?? c.application_name ?? '',
                      }
                    : c,
                );
              }}
              helperText={
                (availableApplications.data?.applications ?? []).length === 0
                  ? 'Every application already has a cost record.'
                  : ' '
              }
            >
              {(availableApplications.data?.applications ?? []).map((app) => (
                <MenuItem key={app.id} value={app.id}>
                  {app.application_id_code} — {app.application_name}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              label="Application Name"
              size="small"
              fullWidth
              required
              value={adding?.application_name ?? ''}
              onChange={(e) =>
                setAdding((c) => (c ? { ...c, application_name: e.target.value } : c))
              }
            />

            {COST_INPUTS.map(({ key, label }) => (
              <TextField
                key={key}
                label={label}
                size="small"
                fullWidth
                type="number"
                value={adding?.[key] ?? '0'}
                onChange={(e) => setAdding((c) => (c ? { ...c, [key]: e.target.value } : c))}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
                inputProps={{ min: 0, step: 1000 }}
              />
            ))}

            <TextField
              select
              label="TCO Status"
              size="small"
              fullWidth
              value={adding?.tco_status ?? 'Not Defined'}
              onChange={(e) => setAdding((c) => (c ? { ...c, tco_status: e.target.value } : c))}
            >
              {TCO_STATUSES.map((status) => (
                <MenuItem key={status} value={status}>
                  {status}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setAdding(undefined)} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={busy}
            onClick={() =>
              void run(async () => {
                await api.apr.createRunRate(adding as Record<string, unknown>);
                setAdding(undefined);
                availableApplications.reload();
                setNotice('Cost record added.');
              })
            }
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={Boolean(confirmDelete)}
        title="Delete this cost record?"
        message={`The run rate for ${confirmDelete?.application_name ?? 'this application'} will no longer be counted in the portfolio total.`}
        confirmLabel="Delete"
        destructive
        busy={busy}
        onConfirm={() =>
          void run(async () => {
            await api.apr.deleteRunRate(confirmDelete!.id);
            setConfirmDelete(undefined);
            setNotice('Cost record deleted.');
          })
        }
        onCancel={() => setConfirmDelete(undefined)}
      />

      <ConfirmDialog
        open={confirmWipe}
        title="Delete all cost records?"
        message={`All ${number(rows.length)} cost records will be retired, removing ${formatCurrency(
          rows.reduce((t, r) => t + (r.total_annual_cost || 0), 0),
        )} from the portfolio run rate.`}
        confirmLabel="Delete all"
        destructive
        busy={busy}
        onConfirm={() =>
          void run(async () => {
            const { deletedCount } = await api.apr.deleteAllRunRate();
            setConfirmWipe(false);
            setNotice(
              `${number(deletedCount)} cost record${deletedCount === 1 ? '' : 's'} deleted.`,
            );
          })
        }
        onCancel={() => setConfirmWipe(false)}
      />
    </Box>
  );
}
